import { lazy, Suspense } from "react";
import { createBrowserRouter, RouterProvider } from "react-router-dom";

import { RouteFallback } from "./components/RouteFallback";
import { RequirePermission } from "./components/RequirePermission";
import { LandingRedirect } from "./components/LandingRedirect";
import { AppShell } from "./AppShell";
import {
  ADMIN_PAGE_PERMISSION,
  PortalPermission,
} from "@/features/auth/access-config";

const NotFoundPage = lazy(() => import("@/features/NotFound/NotFoundPage"));
const PlannerPage = lazy(() =>
  import("@/features/planner/PlannerPage").then((module) => ({
    default: module.PlannerPage,
  })),
);
const PlannerFullviewPage = lazy(() =>
  import("@/features/planner/PlannerFullviewPage").then((module) => ({
    default: module.PlannerFullviewPage,
  })),
);
const PlannerFullviewTablePage = lazy(() =>
  import("@/features/planner/PlannerFullviewTablePage").then((module) => ({
    default: module.PlannerFullviewTablePage,
  })),
);
const MobileDeviceAssignmentPage = lazy(() =>
  import("@/features/planner/MobileDeviceAssignmentPage").then((module) => ({
    default: module.MobileDeviceAssignmentPage,
  })),
);
const TagsPage = lazy(() =>
  import("@/features/vehicle-tags/TagsPage").then((module) => ({
    default: module.TagsPage,
  })),
);
const MaintenancePage = lazy(() =>
  import("@/features/maintenance/MaintenancePage").then((module) => ({
    default: module.MaintenancePage,
  })),
);
const BookingsDateRangeReportPage = lazy(() =>
  import("@/features/reports/pages/BookingsDateRangeReportPage").then(
    (module) => ({
      default: module.BookingsDateRangeReportPage,
    }),
  ),
);
const UsersPage = lazy(() =>
  import("@/features/users/UsersPage").then((module) => ({
    default: module.UsersPage,
  })),
);
const RolesPage = lazy(() =>
  import("@/features/roles/RolesPage").then((module) => ({
    default: module.RolesPage,
  })),
);
const ExamplePage = lazy(() => import("@/features/Example/ExamplePage"));
const VersionPage = lazy(() =>
  import("@/features/version/VersionPage").then((module) => ({
    default: module.VersionPage,
  })),
);
const TrackerLinkLookupPage = lazy(() =>
  import("@/features/tracker-links/TrackerLinkLookupPage").then((module) => ({
    default: module.TrackerLinkLookupPage,
  })),
);

// Exported so routing/permission wiring can be asserted in tests (e.g. that
// /planner/fullview is mounted behind RequirePermission).
export const routes = [
  {
    path: "/version",
    element: (
      <Suspense fallback={<RouteFallback />}>
        <VersionPage />
      </Suspense>
    ),
  },
  {
    // Full-screen TV view — the bus **list/table** (service-desk#81). Rendered
    // without the app shell (no sidebar/topbar chrome), still behind Keycloak via
    // AuthProvider AND the same planner-view permission as /planner (it fetches
    // the same saved assignments/vehicles, so an authenticated user lacking
    // PlannerView must not reach it via direct URL).
    path: "/planner/fullview",
    element: (
      <Suspense fallback={<RouteFallback />}>
        <RequirePermission permission={PortalPermission.PlannerView}>
          <PlannerFullviewTablePage />
        </RequirePermission>
      </Suspense>
    ),
  },
  {
    // The previous full-screen CARD board, preserved (service-desk#81 backed up
    // the board here when the table became the default full-screen view). Same
    // chrome-less + PlannerView-gated treatment as the table above.
    path: "/planner/fullscreencards",
    element: (
      <Suspense fallback={<RouteFallback />}>
        <RequirePermission permission={PortalPermission.PlannerView}>
          <PlannerFullviewPage />
        </RequirePermission>
      </Suspense>
    ),
  },
  {
    element: (
      <Suspense fallback={<RouteFallback />}>
        <AppShell />
      </Suspense>
    ),
    children: [
      { path: "/", element: <LandingRedirect /> },
      {
        path: "/planner",
        element: (
          <RequirePermission permission={PortalPermission.PlannerView}>
            <PlannerPage />
          </RequirePermission>
        ),
      },
      {
        // mobile device Assignment (SD70). In-shell editing page — same PlannerView gate
        // as /planner; the dropdown + Save are additionally gated on
        // PlannerChange inside the page (read-only without it).
        path: "/planner/mobile-devices",
        element: (
          <RequirePermission permission={PortalPermission.PlannerView}>
            <MobileDeviceAssignmentPage />
          </RequirePermission>
        ),
      },
      {
        // Tag catalog management (SD-63). Gated on manage_tags — Managers/Admins
        // only; view + add/edit/remove all require this one permission.
        path: "/planner/tags",
        element: (
          <RequirePermission permission={PortalPermission.PlannerManageTags}>
            <TagsPage />
          </RequirePermission>
        ),
      },
      {
        path: "/maintenance",
        element: (
          <RequirePermission permission={PortalPermission.MaintenanceView}>
            <MaintenancePage />
          </RequirePermission>
        ),
      },
      {
        path: "/reports/bookings-date-range",
        element: (
          <RequirePermission permission={PortalPermission.ReportView}>
            <BookingsDateRangeReportPage />
          </RequirePermission>
        ),
      },
      {
        // Users screen — Administration section, Admin-only (see
        // ADMIN_PAGE_PERMISSION). Managers no longer reach this page.
        path: "/users",
        element: (
          <RequirePermission permission={ADMIN_PAGE_PERMISSION}>
            <UsersPage />
          </RequirePermission>
        ),
      },
      {
        // Roles & Permissions screen — Administration section, Admin-only
        // (see ADMIN_PAGE_PERMISSION). Managers no longer reach this page.
        path: "/roles",
        element: (
          <RequirePermission permission={ADMIN_PAGE_PERMISSION}>
            <RolesPage />
          </RequirePermission>
        ),
      },
      { path: "/example", element: <ExamplePage /> },
      {
        path: "/tracker-links",
        element: (
          <RequirePermission permission={PortalPermission.GenerateTrackerLink}>
            <TrackerLinkLookupPage />
          </RequirePermission>
        ),
      },
      { path: "*", element: <NotFoundPage /> },
    ],
  },
];

const router = createBrowserRouter(routes);

export function AppRouter() {
  return <RouterProvider router={router} />;
}
